import React, { useState, useRef, useEffect } from 'react';
import ReactQuill, { Quill } from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import axios from 'axios';
import { Navigate, useNavigate } from 'react-router-dom';
import useEditArticle from './useEditArticle';
import toast from 'react-hot-toast';
import ApiService from '../../../service/ApiService';
import { apiPaths } from '../../../service/apiPaths';
import { Autocomplete, TextField, Chip } from '@mui/material';
import {
  normalizeHtmlMediaSources,
  normalizeMediaUrl,
} from "../../../utils/normalizeMediaUrl";

// FIXED VIDEO BLOT - Register it with a different approach
const BlockEmbed = Quill.import('blots/block/embed');

class VideoBlot extends BlockEmbed {
  static create(url: string) {
    const node = super.create();
    node.setAttribute('controls', 'true');
    node.setAttribute('preload', 'metadata');
    node.setAttribute('style', 'width: 100%; max-width: 100%; height: auto; display: block; margin: 10px auto; border-radius: 8px;');

    // Add source element for better browser compatibility
    const source = document.createElement('source');
    source.setAttribute('src', url);
    source.setAttribute('type', 'video/mp4');
    node.appendChild(source);

    // Fallback text
    const fallbackText = document.createTextNode('Your browser does not support the video tag.');
    node.appendChild(fallbackText);

    return node;
  }

  static value(node: HTMLElement) {
    const source = node.querySelector('source');
    return source ? source.getAttribute('src') : node.getAttribute('src');
  }

  static formats(node: HTMLElement) {
    const format: any = {};
    if (node.hasAttribute('height')) {
      format.height = node.getAttribute('height');
    }
    if (node.hasAttribute('width')) {
      format.width = node.getAttribute('width');
    }
    return format;
  }
}

VideoBlot.blotName = 'customVideo';
VideoBlot.tagName = 'video';

// FORCE REGISTRATION - This is the key fix!
try {
  Quill.register(VideoBlot, true); // Force override existing registration
  console.log('Custom video blot registered successfully');
} catch (error) {
  console.error('Error registering video blot:', error);
}

interface EditArticleProps {
  articleId: string;
  setSelectedArticleId: (id: string | null) => void;
  onArticleUpdated?: () => void;
}

const EditArticle: React.FC<EditArticleProps> = ({ articleId, setSelectedArticleId, onArticleUpdated }) => {
  const [title, setTitle] = useState<string>('');
  const [content, setContent] = useState<string>('');
  const [intro, setIntro] = useState<string>('');
  // const [introImage, setIntroImage] = useState<string>('');
  const [standard, setStandard] = useState('');
  const [courseName, setCourseName] = useState('');
  const [categoryIds, setCategoryIds] = useState<string[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [filteredCourses, setFilteredCourses] = useState<any[]>([]);
  const [submitLoader, setSubmitLoader] = useState<boolean>(false);

  const { courses, standards, categories, fetchCoursesAndLanguages } = useEditArticle()
  const navigate = useNavigate()
  const quillRef = useRef<ReactQuill | null>(null);

  // Fetch article data with proper content handling
  useEffect(() => {
    const fetchArticle = async () => {
      try {
        const response = await ApiService({
          method: "GET",
          endpoint: `${apiPaths.getArticle}/${articleId}`
        });
        const article = response;

        console.log('FETCHED ARTICLE:', article);
        console.log('ARTICLE CONTENT TYPE:', typeof article.content);
        console.log('ARTICLE CONTENT:', article.content);

        setTitle(article.title);
        setIntro(article.intro);
        setStandard(article.standard);
        setCourseName(article.courseName);

        // Extract category_id array from the article
        if (article.category_id && Array.isArray(article.category_id)) {
          setCategoryIds(article.category_id.map((cat: any) => cat._id || cat));
        }

        // Handle different content formats
        if (typeof article.content === 'string') {
          // If it's HTML string, use it directly
          console.log('CONTENT IS STRING - USING AS IS');
          setContent(normalizeHtmlMediaSources(article.content));
        } else if (article.content && typeof article.content === 'object') {
          // If it's Delta object, we need to set it differently
          console.log('CONTENT IS DELTA OBJECT - NEED TO HANDLE SPECIALLY');

          // Wait for editor to be ready, then set Delta content
          setTimeout(() => {
            const editor = quillRef.current?.getEditor();
            if (editor) {
              console.log('SETTING DELTA CONTENT TO EDITOR');
              editor.setContents(article.content);
              // Get the HTML after setting Delta
              const htmlContent = normalizeHtmlMediaSources(editor.root.innerHTML);
              console.log('CONVERTED HTML:', htmlContent);
              setContent(htmlContent);
            }
          }, 1000);
        } else {
          console.log('CONTENT IS EMPTY OR UNKNOWN FORMAT');
          setContent('');
        }

        setLoading(false);
      } catch (error) {
        console.error('Error fetching article:', error);
        setLoading(false);
      }
    };

    fetchArticle();
    fetchCoursesAndLanguages()
  }, [articleId]);

  // EXACT SAME AS CREATEARTICLE
  useEffect(() => {
    if (standard) {
      const newCourses = courses.filter((course) => course.standard === standard)
      setFilteredCourses(newCourses)
    } else {
      setFilteredCourses([])
    }
  }, [standard])

  useEffect(() => {
    setFilteredCourses(courses)
  }, [courses])

  // EXACT SAME handleImageUpload FROM CREATEARTICLE
  const handleImageUpload = async (file: File) => {
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await ApiService({
        method: "POST",
        endpoint: apiPaths.uploadFile,
        headers: {
          "Content-type": "multipart/form-data"
        },
        data: formData
      })
      console.log(response)
      const imageUrl = normalizeMediaUrl(response.file);
      console.log(imageUrl)

      const editor = quillRef.current?.getEditor();
      const range = editor?.getSelection();
      if (editor && range) {
        editor.insertEmbed(range.index, 'image', imageUrl);
        editor.insertText(range.index + 1, '\n');
        editor.setSelection(range.index + 2, 0);
      }
    } catch (error) {
      console.error('Error uploading image:', error);
    }
  };

  // FIXED handleVideoUpload - The issue was it was treating video URL as image
  const handleVideoUpload = async (file: File) => {
    console.log('VIDEO UPLOAD STARTED');
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await ApiService({
        method: "POST",
        endpoint: apiPaths.uploadFile,
        headers: {
          "Content-type": "multipart/form-data"
        },
        data: formData
      })
      console.log('Upload response:', response);
      const videoUrl = response.file;
      console.log('Video URL:', videoUrl);

      const editor = quillRef.current?.getEditor();
      console.log('EDITOR:', editor);

      const range = editor?.getSelection();
      console.log('SELECTION RANGE:', range);

      if (editor && range) {
        console.log('INSERTING VIDEO WITH CUSTOM BLOT...');

        // CRITICAL: Make sure we're using the custom video blot, not image
        // Check if URL is actually a video
        if (videoUrl.includes('.mp4') || videoUrl.includes('video/upload')) {
          console.log('CONFIRMED VIDEO URL - USING CUSTOM VIDEO BLOT');
          editor.insertEmbed(range.index, 'customVideo', videoUrl);
        } else {
          console.log('URL MIGHT NOT BE VIDEO - USING IMAGE FALLBACK');
          editor.insertEmbed(range.index, 'image', videoUrl);
        }

        editor.insertText(range.index + 1, '\n');
        editor.setSelection(range.index + 2, 0);

        console.log('VIDEO INSERTED');
        console.log('EDITOR HTML AFTER INSERT:', editor.root.innerHTML);
        console.log('EDITOR DELTA AFTER INSERT:', editor.getContents());

        // Force update content state
        const newContent = editor.root.innerHTML;
        console.log('FORCING CONTENT UPDATE:', newContent);
        setContent(newContent);

      } else {
        console.log('NO EDITOR OR RANGE');
      }
    } catch (error) {
      console.error('Error uploading video:', error);
      toast.error('Failed to upload video. Please try again.');
    }
  };

  // EXACT SAME handleInsertImage FROM CREATEARTICLE
  const handleInsertImage = () => {
    const fileInput = document.createElement('input');
    fileInput.setAttribute('type', 'file');
    fileInput.setAttribute("accept", "image/*");
    fileInput.click();

    fileInput.onchange = async (e) => {
      const target = e.target as HTMLInputElement;
      const file = target?.files?.[0];
      if (file) {
        setIsUploading(true);
        await handleImageUpload(file);
        setIsUploading(false);
      }
    };
  };

  // EXACT SAME handleInsertVideo FROM CREATEARTICLE
  const handleInsertVideo = () => {
    const fileInput = document.createElement('input');
    fileInput.setAttribute('type', 'file');
    fileInput.setAttribute('accept', 'video/mp4,video/webm,video/ogg');
    fileInput.click();

    fileInput.onchange = async (e) => {
      const target = e.target as HTMLInputElement;
      const file = target?.files?.[0];
      if (file) {
        if (!file.type.startsWith('video/')) {
          toast.error('Please select a valid video file');
          return;
        }

        const maxSize = 100 * 1024 * 1024; // 100MB
        if (file.size > maxSize) {
          toast.error('Video file is too large. Please select a file smaller than 100MB.');
          return;
        }

        setIsUploading(true);
        const toastId = toast.loading('Uploading video... This may take a while.');

        try {
          await handleVideoUpload(file);
          toast.success('Video uploaded successfully!', { id: toastId });
        } catch (err) {
          toast.error('Failed to upload video.', { id: toastId });
          console.error('Video upload error:', err);
        } finally {
          setIsUploading(false);
        }
      }
    };
  };

  // EXACT SAME handleGifInsert FROM CREATEARTICLE
  const handleGifInsert = () => {
    const fileInput = document.createElement('input');
    fileInput.setAttribute('type', 'file');
    fileInput.setAttribute('accept', 'image/gif');
    fileInput.click();

    fileInput.onchange = async (e) => {
      const target = e.target as HTMLInputElement;
      const file = target?.files?.[0];
      const formData = new FormData();

      if (file) {
        formData.append("file", file);
        setIsUploading(true);

        try {
          const response = await ApiService({
            method: "POST",
            endpoint: apiPaths.uploadFile,
            headers: {
              "Content-type": "multipart/form-data"
            },
            data: formData
          })
          console.log(response)
          const gifUrl = response.file;

          const editor = quillRef.current?.getEditor();
          const range = editor?.getSelection();
          if (editor && range) {
            editor.insertEmbed(range.index, 'image', gifUrl);
            editor.insertText(range.index + 1, '\n');
            editor.setSelection(range.index + 2, 0);
          }
        } catch (error) {
          console.error('Error uploading GIF:', error);
          alert('Failed to upload GIF. Please try again.');
        } finally {
          setIsUploading(false);
        }
      }
    };
  };

  // MODIFIED submitHandler for UPDATE instead of CREATE
  const submitHandler = async () => {
    if (!title.trim()) {
      toast.error("Title is Required")
      return
    }

    if (!content.trim()) {
      toast.error("Content is required")
      return
    }


    const wordCount = title.trim().split(/\s+/).length;
    if (wordCount > 50) {
      toast.error("Title should not exceed 50 words");
      return;
    }

    setSubmitLoader(true)
    const articleData = {
      title,
      content: quillRef.current?.getEditor().getContents(), // SAME AS CREATEARTICLE
      standard,
      courseName,
      category_id: categoryIds,
      intro
    };

    try {
      const response = await ApiService({
        method: "PUT", // UPDATE instead of POST
        endpoint: `${apiPaths.updateArticle}/${articleId}`,
        data: articleData
      });

      if (response) {
        toast.success("Article Updated Successfully")
        // Refresh the article list with updated data
        if (onArticleUpdated) {
          onArticleUpdated();
        }
        setSelectedArticleId(null)
      } else {
        toast.error("Error Occurred")
      }
    } catch (error) {
      toast.error("Error Occurred")
      console.error('Update error:', error);
    }

    setSubmitLoader(false)
  };

  // EXACT SAME modules FROM CREATEARTICLE
  const modules = {
    toolbar: [
      [{ font: [] }],
      [{ size: ['small', false, 'large', 'huge'] }],
      ['bold', 'italic', 'underline', 'strike'],
      [{ color: [] }, { background: [] }],
      [{ align: [] }],
    ],
    clipboard: {
      matchVisual: false,
    }
  };

  // EXACT SAME formats FROM CREATEARTICLE
  const formats = [
    'header', 'font', 'size',
    'bold', 'italic', 'underline', 'strike',
    'color', 'background',
    'list', 'bullet', 'indent',
    'align',
    'link', 'image', 'customVideo' // Add customVideo to formats
  ];

  // EXACT SAME customStyles FROM CREATEARTICLE
  const customStyles = `
    .editor-wrapper {
      background-color: white;
      border: 1px solid #e2e8f0;
      border-radius: 0.375rem;
      max-width: 100%;
      overflow: hidden;
    }

    .quill {
      background-color: white;
      position: relative;
      display: flex;
      flex-direction: column;
      min-height: 300px;
      height: auto;
    }

    .ql-toolbar {
      background-color: white;
      border-bottom: 1px solid #e2e8f0 !important;
      border-top-left-radius: 0.375rem;
      border-top-right-radius: 0.375rem;
      flex-wrap: wrap;
      padding: 8px;
    }

    .ql-toolbar button {
      margin: 2px;
    }

    .ql-container {
      background-color: white;
      flex: 1;
      overflow: auto;
      border: none !important;
    }

    .ql-editor {
      min-height: 300px;
      height: auto;
      padding: 1rem;
      font-size: 1rem;
      line-height: 1.5;
      overflow-x: hidden;
    }

    .ql-editor img {
      max-width: 100%;
      height: auto !important;
      display: block;
      margin: 1rem auto;
      object-fit: contain;
    }

    /* Custom video styles for the new blot */
    .ql-editor video {
      max-width: 100% !important;
      width: 100% !important;
      height: auto !important;
      display: block !important;
      margin: 10px auto !important;
      border-radius: 8px !important;
    }

    .ql-editor iframe {
      max-width: 100%;
      height: auto;
      aspect-ratio: 16/9;
    }

    .ql-editor p {
      margin-bottom: 1rem;
    }

    @media (max-width: 768px) {
      .ql-toolbar {
        padding: 4px;
      }

      .ql-editor {
        padding: 0.75rem;
        font-size: 0.95rem;
      }

      .editor-wrapper {
        margin: 0 -1rem;
        border-radius: 0;
      }

      .ql-editor video {
        margin: 5px auto !important;
      }
    }
  `;

  if (loading) {
    return <div className="flex justify-center items-center h-64">Loading...</div>;
  }

  return (
    <div className="w-full max-w-4xl mx-auto px-4 sm:px-6 py-4">
      <style>{customStyles}</style>
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-4 gap-4">
        <h2 className="text-xl sm:text-2xl font-semibold">Edit Article</h2>
        <button
          onClick={() => setSelectedArticleId(null)}
          className="px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 w-full sm:w-auto"
        >
          Cancel
        </button>
      </div>

      <div className="space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <select
            value={standard}
            onChange={(e) => setStandard(e.target.value)}
            className="w-full p-3 border border-gray-300 rounded-md"
            required
          >
            <option value="">Select Education</option>
            {standards.map(std => (
              <option key={std.standard} value={std.standard}>{std.standard}</option>
            ))}
          </select>

          <select
            value={courseName}
            onChange={(e) => setCourseName(e.target.value)}
            className="w-full p-3 border border-gray-300 rounded-md"
            disabled={standard.length === 0}
            required
          >
            <option value="">Select Stream</option>
            {filteredCourses.map(course => (
              <option key={course.courseName} value={course.courseName}>{course.courseName}</option>
            ))}
          </select>

          <Autocomplete
            multiple
            options={categories}
            getOptionLabel={(option) => option.category}
            value={categories.filter(cat => categoryIds.includes(cat._id))}
            onChange={(event, newValue) => {
              setCategoryIds(newValue.map(cat => cat._id));
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder={categoryIds.length === 0 ? "Select Categories" : ""}
                variant="outlined"
                required={categoryIds.length === 0}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    padding: '8px',
                  }
                }}
              />
            )}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => (
                <Chip
                  label={option.category}
                  {...getTagProps({ index })}
                  size="small"
                  sx={{ margin: '2px' }}
                />
              ))
            }
            sx={{ width: '100%' }}
          />
        </div>

        <input
          type="text"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="Article Title"
          className="w-full p-3 border border-gray-300 rounded-md text-base sm:text-lg"
        />

        <div className="editor-wrapper">
          <ReactQuill
            ref={quillRef}
            theme="snow"
            value={content}
            onChange={(value) => {
              console.log('CONTENT CHANGED:', value);
              setContent(value);
            }}
            placeholder="Write your article here..."
            modules={modules}
            formats={formats}
          />
        </div>

        {/* DEBUG INFO */}


        <div className="flex flex-col sm:flex-row gap-4 flex-wrap">
          <button
            onClick={handleInsertImage}
            disabled={isUploading || submitLoader}
            className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 disabled:bg-gray-400 disabled:cursor-not-allowed w-full sm:w-auto"
          >
            {isUploading ? 'Uploading...' : 'Insert Image'}
          </button>

          <button
            onClick={handleInsertVideo}
            disabled={isUploading || submitLoader}
            className="px-4 py-2 bg-red-500 text-white rounded-md hover:bg-red-600 disabled:bg-gray-400 disabled:cursor-not-allowed w-full sm:w-auto"
          >
            {isUploading ? 'Uploading...' : 'Insert Video'}
          </button>

          <button
            onClick={handleGifInsert}
            disabled={isUploading || submitLoader}
            className="px-4 py-2 bg-teal-500 text-white rounded-md hover:bg-purple-600 disabled:bg-gray-400 disabled:cursor-not-allowed w-full sm:w-auto"
          >
            {isUploading ? 'Uploading...' : 'Insert GIF'}
          </button>

          <button
            onClick={submitHandler}
            className={`px-8 py-2 bg-green-500 text-white font-bold rounded-md hover:bg-green-600 w-full sm:w-auto ${isUploading || submitLoader ? "opacity-50 cursor-not-allowed" : ""}`}
            disabled={isUploading || submitLoader}
          >
            {submitLoader ? 'Updating..' : 'Update Article'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default EditArticle;